'use client'

import { useRef, useState, useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { track } from '../lib/analytics'
import { arsenalStore } from '../store/arsenalStore'
import { openCV } from '../store/cvStore'
import { hasWebGL } from '../lib/webgl'

gsap.registerPlugin(ScrollTrigger)

/* ── Skill rows — each word is a MOLD the particle field pours into (desktop).
     Spec = the toolchain; Proof = one real, verifiable fact. ── */
const SKILLS: {
  word: string
  spec: string
  proof: string
}[] = [
  {
    word: 'FULLSTACK',
    spec: 'React · Next.js · Django · Node · PostgreSQL · REST APIs · GraphQL · WebSockets',
    proof: '100+ builds compiled → production',
  },
  {
    word: 'INTELLIGENCE',
    spec: 'Custom AI Models · LLMs · RAG Pipelines · Agentic Systems · Automation',
    proof: '400+ engineers trained on the stack',
  },
  {
    word: 'CLOUD',
    spec: 'AWS · Docker · Terraform · CI/CD · Huawei Cloud · DevOps',
    proof: 'Infrastructure that survives launch day',
  },
]

const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'

/* ── Reusable hex SVG (identity element) ── */
function HexIcon({ size = 24, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" className={className}>
      <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill="none" stroke="var(--color-fire)" strokeWidth="6" />
    </svg>
  )
}

/**
 * SCENE 3: THE ARSENAL → "THE FORGE"
 * Giant outline words are MOLDS: on desktop the HexField particles pour into the
 * active word white-hot and cool to fire (phase 2 targets = sampled word glyphs,
 * locked to each mold's DOM rect). Scrolling to the next word re-forges the
 * cloud — contract to a spark, bloom out as the next word. On mobile /
 * reduced-motion (no WebGL) the words render solid-filled instead.
 */
export default function Arsenal() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeWord, setActiveWord] = useState(0)
  const activeWordRef = useRef(0) // guards setActiveWord (can be 0..SKILLS.length; == length means "scrolled past all")
  // Mirrors the App.tsx WebGL gate: desktop AND motion-ok → particles fill the molds.
  const [hasParticles, setHasParticles] = useState(false)
  const edge = 'clamp(1.5rem, 4vw, 3.5rem)'

  // ── Forge-threshold HUD (fixed line at 45vh) — updated imperatively from the
  //    scroll ScrollTrigger so scrolling never triggers a React re-render. ──
  const forgeRef = useRef<HTMLDivElement>(null)
  const forgeCastRef = useRef<HTMLSpanElement>(null)
  const forgeTempRef = useRef<HTMLSpanElement>(null)
  const lastTempRef = useRef(-1)
  const lastCastRef = useRef(-1)

  // ── Per-row horizontal stagger — breaks the flush-left template read
  //    (flush-left → indented → pushed-right). Short words carry the big offset. ──
  const ROW_OFFSETS = ['0px', 'clamp(0.75rem, 5vw, 6rem)', 'clamp(1.5rem, 12vw, 13rem)']

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)')
    const evaluate = () => setHasParticles(mq.matches && !rm.matches && hasWebGL())
    evaluate()
    mq.addEventListener('change', evaluate)
    rm.addEventListener('change', evaluate)
    return () => {
      mq.removeEventListener('change', evaluate)
      rm.removeEventListener('change', evaluate)
    }
  }, [])

  useGSAP(
    () => {
      const ctx = containerRef.current
      if (!ctx) return

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      /* ── Forge threshold overlay: hidden until the crossing band is on screen ── */
      if (forgeRef.current) gsap.set(forgeRef.current, { autoAlpha: 0 })
      ScrollTrigger.create({
        trigger: ctx,
        start: 'top 55%',
        end: 'bottom 45%',
        onToggle: (self) => {
          if (!forgeRef.current) return
          gsap.to(forgeRef.current, {
            autoAlpha: self.isActive ? 1 : 0,
            duration: reduce ? 0 : 0.4,
            ease: 'power2.out',
          })
        },
      })

      /* ── Scene header slide-in ── */
      gsap.fromTo(
        ctx.querySelector('.arsenal-header'),
        { y: -20, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.6, ease: 'power3.out',
          scrollTrigger: { trigger: ctx, start: 'top 80%', toggleActions: 'play none none reverse' },
        },
      )

      /* ── Rows: mold + readout reveals ── */
      const rows = ctx.querySelectorAll<HTMLElement>('.skill-row')
      rows.forEach((row, i) => {
        gsap.fromTo(
          row.querySelector('.skill-outline'),
          { y: 60, opacity: 0 },
          {
            y: 0, opacity: 1, duration: 1, ease: 'power3.out',
            scrollTrigger: { trigger: row, start: 'top 90%', toggleActions: 'play none none reverse' },
            delay: i * 0.1,
          },
        )
        gsap.fromTo(
          row.querySelector('.skill-desc'),
          { opacity: 0, y: 20 },
          {
            opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
            scrollTrigger: { trigger: row, start: 'top 80%', toggleActions: 'play none none reverse' },
            delay: 0.2 + i * 0.1,
          },
        )
      })

      /* ── Active word = the mold nearest the forge line (45% viewport height).
           Drives BOTH the particle engine (arsenalStore) and the DOM state. ── */
      ScrollTrigger.create({
        trigger: ctx,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: () => {
          const lineY = window.innerHeight * 0.45
          let best = 0
          let bestD = Infinity
          let bestCenter = 0
          for (let i = 0; i < SKILLS.length; i++) {
            const el = document.getElementById(`arsenal-word-${i}`)
            if (!el) continue
            const r = el.getBoundingClientRect()
            const c = r.top + r.height / 2
            const d = Math.abs(c - lineY)
            if (d < bestD) { bestD = d; best = i; bestCenter = c }
          }
          // Particles follow the nearest word.
          if (arsenalStore.word !== best) arsenalStore.word = best

          // ── Forge readout: temperature climbs as the nearest word crosses the
          //    line (peak ~1540°C = molten iron, right at the threshold). Written
          //    straight to the DOM, guarded by refs so it never re-renders React. ──
          const dist = Math.abs(bestCenter - lineY)
          const prox = Math.max(0, 1 - dist / (window.innerHeight * 0.42))
          const temp = Math.round(720 + prox * 820)
          if (temp !== lastTempRef.current) {
            lastTempRef.current = temp
            if (forgeTempRef.current) forgeTempRef.current.textContent = String(temp)
          }
          // DOM cast state: a word above the active one (i < activeWord) fills as a
          // passed cast. The LAST word has nothing above it, so once you scroll past
          // its centre, bump the active index PAST the end so it fills too — this is
          // what keeps CLOUD lit as you move on into The Path.
          const active = best === SKILLS.length - 1 && bestCenter < lineY ? SKILLS.length : best
          // CAST N/total: the word currently at (or last through) the threshold.
          const castNum = Math.min(active + 1, SKILLS.length)
          if (castNum !== lastCastRef.current) {
            lastCastRef.current = castNum
            if (forgeCastRef.current) forgeCastRef.current.textContent = `${castNum}/${SKILLS.length}`
          }
          if (activeWordRef.current !== active) {
            activeWordRef.current = active
            setActiveWord(active)
          }
        },
        onLeave: () => { arsenalStore.word = SKILLS.length - 1; activeWordRef.current = SKILLS.length; setActiveWord(SKILLS.length) },
        onLeaveBack: () => { arsenalStore.word = 0; activeWordRef.current = 0; setActiveWord(0) },
      })

      /* ── Download CV button ── */
      gsap.fromTo(
        ctx.querySelector('.arsenal-explore'),
        { scale: 0.9, opacity: 0 },
        {
          scale: 1, opacity: 1, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: ctx, start: 'top 20%', toggleActions: 'play none none reverse' },
        },
      )
    },
    { scope: containerRef },
  )

  // ── White-hot flash: the word arriving AT the threshold flares near-white then
  //    settles to fire. Only in non-particle mode (on desktop the HexField owns
  //    the active fill). Reduced motion → no flash, word sits at its settled fire. ──
  useEffect(() => {
    if (hasParticles) return
    if (activeWord < 0 || activeWord >= SKILLS.length) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const inner = document.getElementById(`arsenal-word-${activeWord}`)
    const el = inner?.parentElement // the .skill-outline span carries the fill
    if (!el) return
    gsap.fromTo(
      el,
      { WebkitTextFillColor: '#FFF7ED', color: '#FFF7ED' },
      { WebkitTextFillColor: '#FF6B00', color: '#FF6B00', duration: 0.7, ease: 'power2.out' },
    )
    return () => { gsap.killTweensOf(el) }
  }, [activeWord, hasParticles])

  return (
    <section
      ref={containerRef}
      id="arsenal"
      data-scene="arsenal"
      className="relative w-full"
      style={{
        background: 'var(--color-abyss)',
        paddingTop: 'clamp(4rem, 10vh, 8rem)',
        paddingBottom: 'clamp(4rem, 10vh, 8rem)',
        minHeight: '90vh',
      }}
    >
      {/* ── Background dot grid — gray base ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle 1.5px at center, rgba(140,140,140,0.22) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
        aria-hidden="true"
      />
      {/* ── Background dot grid — orange center overlay ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle 1.5px at center, rgba(255,107,0,0.5) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse 70% 80% at 50% 50%, black 0%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 80% at 50% 50%, black 0%, transparent 100%)',
        }}
        aria-hidden="true"
      />

      {/* ── Ambient glow — re-anchored to the 45% forge line so the brightest
             point sits where the words ignite (behind the words) ── */}
      <div
        className="absolute pointer-events-none"
        aria-hidden="true"
        style={{
          top: '45%',
          left: '50%',
          width: '60vw',
          height: '50vh',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(255,107,0,0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* ── Forge threshold — a STATIONARY fire rule fixed at 45vh that the words
             cross. Live readout casts the current word + a rising temperature. ── */}
      <div
        ref={forgeRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: '45vh',
          zIndex: 6,
          opacity: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            height: 1,
            background: 'linear-gradient(90deg, transparent, var(--color-fire) 18%, var(--color-fire) 82%, transparent)',
            opacity: 0.55,
            boxShadow: '0 0 12px rgba(255,107,0,0.4)',
          }}
        />
        <div
          className="font-mono uppercase flex items-center justify-between"
          style={{
            paddingLeft: edge,
            paddingRight: edge,
            marginTop: '0.45rem',
            fontSize: 'clamp(0.55rem, 0.75vw, 0.66rem)',
            letterSpacing: '0.22em',
            color: 'var(--color-stone)',
          }}
        >
          <span>Forge Threshold · Y 45.0</span>
          <span style={{ color: 'var(--color-fire)' }}>
            Forge · Cast&nbsp;
            <span ref={forgeCastRef}>1/{SKILLS.length}</span>
            &nbsp;· <span ref={forgeTempRef}>720</span>&deg;C
          </span>
        </div>
      </div>

      {/* ── Scene header ── */}
      <div
        className="arsenal-header flex items-center gap-3 opacity-0"
        style={{ paddingLeft: edge, paddingRight: edge, marginBottom: 'clamp(3rem, 6vh, 5rem)', position: 'relative', zIndex: 3 }}
      >
        <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>03</span>
        <HexIcon size={10} />
        <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>FORGE</span>
        <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>/ what i do</span>
      </div>

      {/* ── Skill rows — the molds ── */}
      <div className="relative" style={{ paddingLeft: edge, paddingRight: edge, zIndex: 3 }}>
        {SKILLS.map((skill, i) => {
          const on = i === activeWord // casting — the mold at the threshold
          const passed = i < activeWord // above the line, cooling to ember
          return (
            <div
              key={i}
              className="skill-row group relative"
              style={{
                marginBottom: i < SKILLS.length - 1 ? 'clamp(2.5rem, 5vh, 4rem)' : 0,
                marginLeft: ROW_OFFSETS[i] ?? '0px',
              }}
            >
              <div className="relative w-full" style={{ containerType: 'inline-size' }}>
                {/* The MOLD: outline-only on desktop (particles pour the fill);
                    solid fire fill when there is no particle field. */}
                <span
                  className="skill-outline font-display font-extrabold select-none leading-[0.85] tracking-[-0.04em] opacity-0"
                  style={{
                    // floor 1.5rem: INTELLIGENCE (12 glyphs, nowrap) must fit a 390px
                    // phone — a 2.4rem floor overflowed it by ~96px
                    fontSize: 'clamp(1.5rem, 7.5cqi, 12rem)',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    ...(hasParticles
                      ? {
                          // Particles pour the active fill; the mold outline just
                          // reads temperature: bright at the line, cold below,
                          // dimmed once it has passed (the ember overlay lights it).
                          WebkitTextFillColor: 'transparent',
                          WebkitTextStroke: on
                            ? '1px rgba(255,107,0,0.5)'
                            : passed
                              ? '1px rgba(255,107,0,0.08)'
                              : '1px rgba(255,107,0,0.14)',
                          transition: '-webkit-text-stroke-color 0.6s ease',
                        }
                      : {
                          // No WebGL → DOM carries the whole thermal stack:
                          // cold outline (below) → fire (at the line) → ember (passed).
                          color: on ? 'var(--color-fire)' : passed ? '#b34a00' : 'transparent',
                          WebkitTextFillColor: on ? 'var(--color-fire)' : passed ? '#b34a00' : 'transparent',
                          WebkitTextStroke: on || passed ? '0' : '1px rgba(255,107,0,0.28)',
                          transition: 'color 0.6s ease, -webkit-text-fill-color 0.6s ease, -webkit-text-stroke-color 0.6s ease',
                        }),
                  }}
                >
                  {/* inline-block inner span = the exact glyph rect the particles lock onto */}
                  <span id={`arsenal-word-${i}`} style={{ display: 'inline-block' }}>{skill.word}</span>
                </span>

                {/* PASSED cast — a word above the active one (already scrolled past)
                    stays lit as a solid fill; scrolling back up empties it again.
                    The active word is filled by the particle field; words below are
                    bare outline molds. */}
                {hasParticles && (
                  <span
                    aria-hidden
                    className="font-display font-extrabold select-none leading-[0.85] tracking-[-0.04em] absolute inset-0 pointer-events-none"
                    style={{
                      fontSize: 'clamp(1.5rem, 7.5cqi, 12rem)',
                      display: 'block',
                      whiteSpace: 'nowrap',
                      // A passed cast has cooled off the peak: dim ember, muted glow.
                      color: '#b34a00',
                      WebkitTextFillColor: '#b34a00',
                      textShadow: '0 0 20px rgba(179,74,0,0.22)',
                      opacity: passed ? 1 : 0,
                      // fade in with a small delay (let the particles morph away first);
                      // empty fast when scrolling back up past it.
                      transition: passed ? 'opacity 0.6s ease 0.2s' : 'opacity 0.3s ease',
                    }}
                  >
                    {skill.word}
                  </span>
                )}

                {/* Spec readout — the HUD payload under each mold */}
                <div
                  className="skill-desc opacity-0 mt-3"
                  style={{ paddingBottom: 'clamp(0.5rem, 1.2vh, 1rem)', zIndex: 4 }}
                >
                  <div
                    style={{ opacity: on || !hasParticles ? 1 : 0.38, transition: 'opacity 0.5s ease' }}
                  >
                    <span
                      className="skill-tags font-mono block"
                      style={{
                        fontSize: 'clamp(0.72rem, 2.6vw, 0.82rem)',
                        letterSpacing: '0.12em',
                        color: 'rgba(255,200,150,0.8)',
                        textShadow: '0 1px 8px rgba(0,0,0,0.9)',
                      }}
                    >
                      <span style={{ color: 'var(--color-stone)' }}>// SPEC&nbsp;&nbsp;</span>
                      {skill.spec}
                    </span>
                    <span
                      className="skill-proof font-mono flex items-center gap-2 uppercase"
                      style={{
                        marginTop: '0.5rem',
                        fontSize: 'clamp(0.66rem, 2.4vw, 0.74rem)',
                        letterSpacing: '0.18em',
                        color: on || !hasParticles ? 'var(--color-fire)' : 'var(--color-stone)',
                        transition: 'color 0.5s ease',
                        textShadow: '0 1px 8px rgba(0,0,0,0.9)',
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 7, height: 8, clipPath: HEX_DOT, flexShrink: 0,
                          background: on || !hasParticles ? 'var(--color-fire)' : 'var(--color-steel-deep)',
                          boxShadow: on && hasParticles ? '0 0 8px var(--color-fire)' : 'none',
                          transition: 'background 0.5s ease',
                        }}
                      />
                      Proof / {skill.proof}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Download CV button ── */}
      <div
        className="arsenal-explore flex items-center justify-center opacity-0"
        style={{ marginTop: 'clamp(2rem, 4vh, 3.5rem)', position: 'relative', zIndex: 3 }}
      >
        <button
          type="button"
          onClick={() => { track('cv_download', { method: 'view_cv' }); openCV() }}
          data-cursor-label="Dossier"
          aria-haspopup="dialog"
          className="group relative flex items-center gap-3 font-mono uppercase"
          style={{
            fontSize: 'clamp(0.6rem, 0.8vw, 0.7rem)',
            letterSpacing: '0.25em',
            color: 'var(--color-fire)',
            padding: 'clamp(0.9rem, 1.4vw, 1rem) clamp(1.25rem, 2vw, 2rem)',
            border: '1px solid rgba(255,107,0,0.3)',
            background: 'transparent',
            cursor: 'pointer',
            transition: 'border-color 0.3s, background 0.3s',
          }}
        >
          <HexIcon size={12} />
          <span>View CV</span>
          {/* Expanding frame — the dossier opens in place, so the old
              "leaves the site" arrow would now be a lie. */}
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:scale-110"
            aria-hidden="true"
          >
            <path d="M3 9V4a1 1 0 011-1h5M21 9V4a1 1 0 00-1-1h-5M3 15v5a1 1 0 001 1h5M21 15v5a1 1 0 01-1 1h-5" />
          </svg>
        </button>
      </div>

      {/* ── Scene divider — pinned to absolute bottom of section ── */}
      <div
        className="h-px absolute bottom-0"
        style={{ left: edge, right: edge, backgroundColor: 'rgba(255,107,0,0.12)', zIndex: 3 }}
      />
    </section>
  )
}
